import React, { useCallback, useEffect, useState } from 'react';
import head from 'lodash/head';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AnniversaryLogo, ArrowForward } from '@yme/assets/icons';
import { Col, Container, GridWrapper } from '@yme/atoms/grid';
import { LOCATION_HEADER, Logo } from '@yme/atoms/logo';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { setHyperDriveEndpoint } from '@yme/common/redux/cart/actions';
import { getCart } from '@yme/common/redux/cart/services';
import { PageLevelNavigation } from '@yme/components/Header/PageLevelNavigation';
import { Search } from '@yme/components/Header/Search';
import { useProfileMenu } from '@yme/components/Header/SignInMenuItem/useProfileMenu';
import { QueryParams } from '@yme/components/ShoppingCart/constants';
import { useAuthorization, useShowModalSessionExpired, useTouchOnly, useWindowSize, useMenuHeight, useKioskHomeUrl, } from '@yme/hooks/index';
import { ButtonLinkWrapper } from '@yme/molecules/buttons';
import { getCurrentCountryRoot, getIs70thLogoEnabled, getIsSearchEnabled, getLanguageRootPath, getPageLevelNavigationData, getRTL, } from '@yme/selectors/common';
import { ComponentHolder, FlexWrapper } from '@yme/tokens/flexTokens';
import { filterNotSupCountries, getQueryParamByName } from '@yme/utils/common';
import { serviceAPI } from '../../utils/api';
import { LocationMismatchBar } from './LocationMismatchBar';
import { Menu } from './Menu';
import { HamburgerButton, MobileMegamenu } from './MobileMegamenu';
import { MenuContext } from './MobileMegamenu/menuContext';
import { useMenuContext } from './MobileMegamenu/useMenuContext';
import { ProfileMenu } from './ProfileMenu';
import { setIsLoadedLogoutIframe, setProfileMenuDataAction } from './ProfileMenu/redux/actions';
import { getAuthorizedStatus, getIsEmptyProfileData } from './ProfileMenu/redux/selectors';
import { useSearch } from './Search';
import { ANNIVERSARY_LOGO_LINK, HEADER_PORTAL_ID } from './constants';
import { getInRiverIds } from './helpers';
import { actions, setHeaderStickyMenuShown, setIsMenuActive, setPageType } from './redux/actions';
import { getHeaderStickyMenuShown, getIsMenuOpen } from './redux/selectors';
import { BackButtonStyled, HeaderContainerStyled, HeaderLogoWrapper, HeaderStyled, HeaderWithSearchStyled, LogoSplitterStyled, ProfileContainerStyled, SplitterStyled, StickyWrapperStyled, } from './styled';
export function Header(props) {
    const { logoLink, inRiverId, hyperdriveEndpoints, labels, clientId, myYamahaLink, callbackEndpointUri, countries, megamenuURL, } = props;
    const currentCountry = useSelector(getCurrentCountryRoot);
    const isEmptyProfileData = useSelector(getIsEmptyProfileData);
    const is70thLogoEnabled = useSelector(getIs70thLogoEnabled);
    const filteredCountries = filterNotSupCountries(countries, currentCountry);
    const dispatch = useDispatch();
    const location = useLocation();
    const [inRiverIds, setInRiverIds] = useState([]);
    const { detectAuthorizedStatus, checkUserSession, isExpiredSession } = useAuthorization();
    const { isLargeDesktopView, isInfiniteDesktopView, isTabletView, isMobileView } = useWindowSize();
    const { isHeaderSearchOpen, onSearchClickHandler } = useSearch();
    const heightOfMenu = useMenuHeight();
    const homeLink = useKioskHomeUrl(logoLink);
    const isAuthorized = useSelector(getAuthorizedStatus);
    const headerStickyMenuShown = useSelector(getHeaderStickyMenuShown);
    const isRightToLeft = useSelector(getRTL);
    const isMenuOpen = useSelector(getIsMenuOpen);
    const pageLevelNavigationData = useSelector(getPageLevelNavigationData);
    const isSearchEnabled = useSelector(getIsSearchEnabled);
    const languageRootPath = useSelector(getLanguageRootPath);
    const { signInHandler } = useProfileMenu();
    const { showModalSessionExpired } = useShowModalSessionExpired();
    const history = useHistory();
    const menuContext = useMenuContext(megamenuURL);
    const { returnToPrev, prevs, setMenuChildren, setPrevs, children, initialChildren } = menuContext;
    const [isSticky, setIsSticky] = useState(false);
    const isTouchDevice = useTouchOnly();
    const isMobile = !isInfiniteDesktopView || isTouchDevice;
    const isShowProfileMenu = !prevs?.length || isTabletView || (isInfiniteDesktopView && isTouchDevice);
    const isShowSearch = isHeaderSearchOpen && isInfiniteDesktopView && !isTouchDevice;
    const anniversaryLogoLink = languageRootPath + ANNIVERSARY_LOGO_LINK;
    useEffect(() => {
        const setIsStickyOnScroll = () => {
            const scrollTop = window.scrollY;
            const documentElement = document.documentElement;
            const scrollMaxY = documentElement.scrollHeight - documentElement.clientHeight;
            if (!isSticky && scrollMaxY !== null && scrollMaxY < heightOfMenu * 2) {
                return;
            }
            const newValue = scrollTop >= heightOfMenu;
            if (newValue === isSticky) {
                return;
            }
            setIsSticky(newValue);
        };
        window.addEventListener('scroll', setIsStickyOnScroll);
        return () => {
            window.removeEventListener('scroll', setIsStickyOnScroll);
        };
    }, [isSticky, dispatch, heightOfMenu]);
    useEffect(() => {
        if (isSticky && !headerStickyMenuShown) {
            dispatch(setHeaderStickyMenuShown(true));
        }
        if (!isSticky && headerStickyMenuShown) {
            dispatch(setHeaderStickyMenuShown(false));
        }
    }, [dispatch, headerStickyMenuShown, isSticky]);
    // TODO: (A.H.) - need to improve and move to the common
    useEffect(() => {
        if (isMenuOpen) {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
            document.body.style.overflow = 'hidden';
            const handleScroll = () => window.scrollTo(scrollLeft, scrollTop);
            window.addEventListener('scroll', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll);
                document.body.style.overflow = 'auto';
            };
        }
        else {
            const handleScroll = () => null;
            window.addEventListener('scroll', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll);
                document.body.style.overflow = 'auto';
            };
        }
    }, [isMenuOpen, isLargeDesktopView]);
    useEffect(() => {
        if (!isMenuOpen && prevs.length > 0) {
            setPrevs([]);
            dispatch(setPageType(''));
            setMenuChildren(initialChildren);
        }
    }, [initialChildren, dispatch, isMenuOpen, prevs, setMenuChildren, setPrevs]);
    useEffect(() => {
        // TODO: create selector for parameters
        dispatch(setProfileMenuDataAction({
            labels,
            clientId,
            myYamahaLink,
            callbackEndpointUri,
        }));
    }, [labels, callbackEndpointUri, clientId, dispatch, myYamahaLink]);
    const signOut = useCallback(() => {
        dispatch(setIsLoadedLogoutIframe(true));
    }, [dispatch]);
    const isCheckoutPage = useCallback(() => {
        return location.pathname.includes('checkout');
    }, [location.pathname]);
    useEffect(() => {
        if (isAuthorized) {
            (async () => {
                await checkUserSession();
            })();
        }
    }, [checkUserSession, isAuthorized, location.hash]);
    useEffect(() => {
        if (!isExpiredSession)
            return;
        if (!isEmptyProfileData) {
            signOut();
            return;
        }
        if (isCheckoutPage()) {
            showModalSessionExpired({
                submitCallback: signInHandler,
            });
        }
    }, [
        isCheckoutPage,
        isEmptyProfileData,
        isExpiredSession,
        showModalSessionExpired,
        signInHandler,
        signOut,
    ]);
    useEffect(() => {
        detectAuthorizedStatus();
    }, [detectAuthorizedStatus]);
    useEffect(() => {
        const hashedCartId = getQueryParamByName(history.location.search, QueryParams.HASHED_CART_ID);
        const cartIdFromUrl = getQueryParamByName(history.location.search, QueryParams.CART_ID);
        dispatch(setHyperDriveEndpoint(hyperdriveEndpoints.getCartById));
        if (!hashedCartId && !cartIdFromUrl) {
            dispatch(getCart(hyperdriveEndpoints.getCartById, true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!children.items) {
            return;
        }
        getInRiverIds(setInRiverIds, head(children.items));
    }, [children.items]);
    useEffect(() => {
        if (inRiverIds.length) {
            dispatch(serviceAPI({
                url: hyperdriveEndpoints.getCategoryProducts,
                method: 'post',
                data: {
                    externalIds: inRiverIds,
                },
            }, 
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            actions));
        }
    }, [dispatch, hyperdriveEndpoints.getCategoryProducts, inRiverIds]);
    const onHamburgerClickHandler = useCallback(() => {
        dispatch(setIsMenuActive(!isMenuOpen));
    }, [dispatch, isMenuOpen]);
    const clickMenuHandler = useCallback(() => {
        dispatch(setIsMenuActive(false));
    }, [dispatch]);
    if (!logoLink) {
        return null;
    }
    const HeaderWithSearch = (React.createElement(Container, { id: HEADER_PORTAL_ID, isRightToLeft: isRightToLeft },
        React.createElement(HeaderWithSearchStyled, null,
            React.createElement(Col, { lg: 2 },
                React.createElement(HeaderLogoWrapper, { isRightToLeft: isRightToLeft }, !prevs?.length || (isInfiniteDesktopView && !isTouchDevice) ? (React.createElement(FlexWrapper, { alignItems: "center" },
                    React.createElement(Logo, { logoLink: homeLink, locationType: LOCATION_HEADER }),
                    !isMobileView && is70thLogoEnabled && (React.createElement(React.Fragment, null,
                        React.createElement(LogoSplitterStyled, null),
                        React.createElement(ButtonLinkWrapper, { buttonLink: anniversaryLogoLink },
                            React.createElement(SVGIcon, { type: AnniversaryLogo, size: ICON_SIZE.sm })))))) : (React.createElement(BackButtonStyled, { onClick: returnToPrev, isRightToLeft: isRightToLeft },
                    React.createElement(SVGIcon, { type: ArrowForward, size: ICON_SIZE.s }),
                    React.createElement("span", null, prevs?.slice()?.pop()?.title))))),
            React.createElement(Col, { lg: 8 }, isShowSearch && (React.createElement(Search, { isRightToLeft: isRightToLeft, isMobile: isMobile, focusable: true }))))));
    return (React.createElement(MenuContext.Provider, { value: menuContext },
        React.createElement(ComponentHolder, null,
            React.createElement(StickyWrapperStyled, { isSticky: isSticky, heightOfMenu: heightOfMenu },
                isShowSearch ? (HeaderWithSearch) : (React.createElement(GridWrapper, { id: HEADER_PORTAL_ID },
                    React.createElement(HeaderStyled, { isRightToLeft: isRightToLeft },
                        React.createElement(HeaderContainerStyled, null,
                            React.createElement(HeaderLogoWrapper, { isRightToLeft: isRightToLeft }, !prevs?.length || (isInfiniteDesktopView && !isTouchDevice) ? (React.createElement(FlexWrapper, { alignItems: "center" },
                                React.createElement(Logo, { logoLink: homeLink, locationType: LOCATION_HEADER }),
                                !isMobileView && is70thLogoEnabled && (React.createElement(React.Fragment, null,
                                    React.createElement(LogoSplitterStyled, null),
                                    React.createElement(ButtonLinkWrapper, { buttonLink: anniversaryLogoLink },
                                        React.createElement(SVGIcon, { type: AnniversaryLogo, size: ICON_SIZE.sm })))))) : (React.createElement(BackButtonStyled, { onClick: returnToPrev, isRightToLeft: isRightToLeft },
                                React.createElement(SVGIcon, { type: ArrowForward, size: ICON_SIZE.s }),
                                React.createElement("span", null, prevs?.slice()?.pop()?.title)))),
                            isLargeDesktopView &&
                                !isTouchDevice &&
                                !isHeaderSearchOpen &&
                                !is70thLogoEnabled && React.createElement(SplitterStyled, null),
                            isInfiniteDesktopView && !isTouchDevice && !isHeaderSearchOpen && (React.createElement(Menu, { items: children?.items, labels: labels, isRightToLeft: isRightToLeft })),
                            isShowProfileMenu && (React.createElement(React.Fragment, null,
                                React.createElement(ProfileContainerStyled, { isRightToLeft: isRightToLeft },
                                    React.createElement(ProfileMenu, { countries: filteredCountries, labels: labels, onSelectorClickHandler: clickMenuHandler, onSearchClickHandler: onSearchClickHandler, isCheckoutPage: isCheckoutPage(), isMobile: isMobile, isSearchEnabled: isSearchEnabled })),
                                isMobile && React.createElement(SplitterStyled, null))),
                            isMobile && (React.createElement(HamburgerButton, { isMenuShowed: isMenuOpen, onClickMenu: onHamburgerClickHandler })))))),
                pageLevelNavigationData?.isDestinationPage && React.createElement(PageLevelNavigation, null)),
            !pageLevelNavigationData?.isDestinationPage && React.createElement(PageLevelNavigation, null),
            React.createElement(LocationMismatchBar, null)),
        React.createElement(MobileMegamenu, { inRiverId: inRiverId, countries: filteredCountries, labels: labels, isMenuShowed: isMenuOpen, isHeaderSticky: isSticky, isSearchEnabled: isSearchEnabled, onClickMenu: clickMenuHandler })));
}
Header.displayName = 'HeaderComponent';
